import { useMemo, useState } from 'react'

import { useIncidentsQuery } from '@/graphql/generated/hooks'
import {
  IncidentOrderField,
  IncidentType,
  OrderDirection,
} from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'

import { IncidentOptionI } from '../../types/types'

const PAGE_SIZE = 10

export const useIncidentSearch = (
  doorId: string | undefined,
  typeFilter: IncidentType | null
) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [pageIndex, setPageIndex] = useState(0)

  const {
    data,
    loading: isLoading,
    error,
  } = useIncidentsQuery({
    variables: {
      filter: {
        deviceIdIn: doorId ? [doorId] : [],
        hasVideoClip: true,
        ...(typeFilter ? { typeIn: [typeFilter] } : {}),
        ...(searchTerm ? { displayIdLike: searchTerm } : {}),
      },
      first: pageIndex,
      last: PAGE_SIZE,
      orderBy: [
        {
          field: IncidentOrderField.CreatedAt,
          direction: OrderDirection.Desc,
        },
      ],
    },
    skip: !doorId,
  })

  const totalCount = data?.incidents?.totalCount ?? 0
  const pageCount = Math.ceil(totalCount / PAGE_SIZE)

  const incidents: IncidentOptionI[] = useMemo(
    () =>
      (data?.incidents?.edges ?? [])
        .map((edge) => edge?.node)
        .filter(Boolean)
        .map((node) => ({
          id: node.id,
          incidentId: node.displayId,
          label: `${node.displayId} | ${formatDateTime(node.createdAt)}`,
          createdAt: node.createdAt,
          status: node.status ?? undefined,
          type: node.type ?? undefined,
        })),
    [data]
  )

  const goToPage = (page: number) => setPageIndex(page)
  const nextPage = () => setPageIndex((p) => Math.min(p + 1, pageCount - 1))
  const previousPage = () => setPageIndex((p) => Math.max(p - 1, 0))

  // Reset to first page when filters change
  const handleSetSearchTerm = (term: string) => {
    setSearchTerm(term)
    setPageIndex(0)
  }

  return {
    incidents,
    isLoading,
    error,
    searchTerm,
    setSearchTerm: handleSetSearchTerm,
    totalCount,
    pageIndex,
    pageSize: PAGE_SIZE,
    pageCount,
    goToPage,
    nextPage,
    previousPage,
  }
}
